import { useCallback, useRef } from "preact/hooks";
import { Swiper } from "swiper";
import { Pagination } from "swiper/modules";
import { clsx } from "clsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import ChevronRightIcon from "~/components/icons/chevron-right.tsx";
import BlueBrickIcon from "~/components/icons/blue-brick.tsx";
import ClipboardIcon from "~/components/icons/clipboard.tsx";
// import BasketIcon from "~/components/icons/basket.tsx";
import ladyFaceSlideImage from "./lady-face-slide.webp";
import couplesFacesSlideImage from "./couples-faces-slide.webp";
import motorbikeSlideImage from "./motorbike-slide.webp";
import catSlideImage from "./cat-slide.webp";
import familySlideImage from "./family-slide.webp";
import "swiper/css";
import "swiper/css/pagination";
import classes from "./intro.module.css";

const swiperImages = [
	{ src: ladyFaceSlideImage, width: 272, height: 272, alt: "Lady face" },
	{ src: motorbikeSlideImage, width: 272, height: 272, alt: "Motorbike" },
	{
		src: couplesFacesSlideImage,
		width: 272,
		height: 363,
		alt: "Couples faces",
	},
	{ src: catSlideImage, width: 272, height: 272, alt: "Cat" },
	{ src: familySlideImage, width: 272, height: 204, alt: "Family" },
];

type IntroProps = {
	readonly onStartTryItOut: () => void;
	readonly onStart: () => void;
	readonly onBuy: () => void;
};

function Intro({ onStartTryItOut, onStart }: IntroProps) {
	const t = useTranslator();
	const swiperRef = useRef<Swiper | undefined>(undefined);
	const setSwiperRef = useCallback(
		(newRef: HTMLDivElement | null) => {
			const { current: previousSwiper } = swiperRef;
			if (previousSwiper) {
				previousSwiper.destroy();
				swiperRef.current = undefined;
			}

			if (newRef === null) {
				return;
			}

			// eslint-disable-next-line no-param-reassign
			newRef.style.visibility = "visible";
			const swiper = new Swiper(newRef, {
				modules: [Pagination],
				initialSlide: Math.floor(swiperImages.length / 2),
				slideToClickedSlide: true,
				slidesPerView: 1.2,
				// spaceBetween: 0,
				breakpoints: {
					400: {
						slidesPerView: 1.5,
					},
					500: {
						slidesPerView: 2,
					},
					800: {
						slidesPerView: 3,
					},
				},
				pagination: {
					el: ".swiper-pagination",
					bulletActiveClass: clsx(
						"swiper-pagination-bullet-active",
						classes["swiper-pagination-bullet-active"],
					),
					bulletClass: clsx(
						"swiper-pagination-bullet",
						classes["swiper-pagination-bullet"],
					),
				},
				centeredSlides: true,
			});
			function renderSlides() {
				swiper.slides.forEach((slide, i) => {
					const indexDiff = i - swiper.activeIndex;
					const indexDiffSign =
						indexDiff === 0 ? 0 : indexDiff / Math.abs(indexDiff);
					const cappedIndexDiff = Math.min(2, Math.abs(indexDiff));
					const rotation =
						indexDiffSign * cappedIndexDiff * cappedIndexDiff * 6;
					const yOffset =
						Math.pow(Math.abs(indexDiff * indexDiff * 4), 1.9) * 0.2 +
						Math.abs(rotation) * 1.2;

					// eslint-disable-next-line no-param-reassign
					slide.style.transform = `rotate(${rotation}deg) translateY(${yOffset}px)`;

					swiper.update();
				});
			}
			swiper.on("activeIndexChange", renderSlides);
			renderSlides();

			swiperRef.current = swiper;
		},
		[swiperRef],
	);

	return (
		<div className={classes["intro-container"]}>
			<div className={classes["intro-bg"]} />
			<div className={classes["intro-content"]}>
				<div ref={setSwiperRef} class={clsx("swiper", classes["swiper"])}>
					<div class="swiper-wrapper">
						{swiperImages.map((swiperImage) => (
							<div
								key={swiperImage.src}
								class={clsx("swiper-slide", classes["swiper-slide"])}
							>
								<img
									src={swiperImage.src}
									width={swiperImage.width}
									height={swiperImage.height}
									alt={swiperImage.alt}
								/>
							</div>
						))}
					</div>
					<div class="swiper-pagination" />
				</div>
				<div className={classes["intro-options"]}>
					<div>Choose an option below to get started.</div>
					<ul className={classes["intro-option-buttons"]}>
						<li id="try-it-out-item">
							<button type="button" onClick={onStartTryItOut}>
								<div className={classes["intro-option-button-icon"]}>
									<BlueBrickIcon />
								</div>
								<div className={classes["intro-option-button-main"]}>
									<div className={classes["intro-option-button-top-text"]}>
										{t("Don’t have kit yet?")}
									</div>
									<div className={classes["intro-option-button-bottom-text"]}>
										{t("Try it out")}
									</div>
								</div>
								<ChevronRightIcon />
							</button>
						</li>
						<li id="i-have-a-kit-item">
							<button type="button" onClick={onStart}>
								<div className={classes["intro-option-button-icon"]}>
									<ClipboardIcon />
								</div>
								<div className={classes["intro-option-button-main"]}>
									<div className={classes["intro-option-button-top-text"]}>
										{t("I have a kit!")}
									</div>
									<div className={classes["intro-option-button-bottom-text"]}>
										{t("Get your Instructions")}
									</div>
								</div>
								<ChevronRightIcon />
							</button>
						</li>
						{/* Coming soon */}
						{/* <li>
							<button type="button" onClick={onBuy}>
								<div className={classes["intro-option-button-icon"]}>
									<BasketIcon />
								</div>
								<div className={classes["intro-option-button-main"]}>
									<div className={classes["intro-option-button-top-text"]}>
										{t("Need a kit?")}
									</div>
									<div className={classes["intro-option-button-bottom-text"]}>
										{t("Buy a kit here")}
									</div>
								</div>
								<ChevronRightIcon />
							</button>
						</li> */}
					</ul>
				</div>
			</div>
		</div>
	);
}

export default Intro;
